// src/pages/Admin/PositionPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FaEdit } from "react-icons/fa";
import axios from "axios";
import Select from "react-select";
import {
  Container,
  Button,
  Form,
  Offcanvas,
  Card,
  Row,
  Col,
  InputGroup,
  Modal,
} from "react-bootstrap";
import dayjs from "dayjs";
import ReactQuill from "react-quill-new";
import "react-quill/dist/quill.snow.css";
import PositionPanel from "./PositionPanel";
import Pagination from "../../components/Pagination";
import useAccess from "../../hooks/useAccess";
import "../../styles/PositionPage.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const currentUserId = parseInt(localStorage.getItem("userId"));

function normalizeSortValue(value) {
  if (value == null) return "";
  if (typeof value === "number") return value;

  const text = String(value).trim();
  if (!text) return "";

  const numeric = Number(text.replace(/,/g, ""));
  if (!Number.isNaN(numeric) && /^[-+]?\d[\d,.]*$/.test(text)) {
    return numeric;
  }

  const dateValue = Date.parse(text);
  if (!Number.isNaN(dateValue) && /(\d{4}|\d{1,2}[/-]\d{1,2})/.test(text)) {
    return dateValue;
  }

  return text.toLowerCase();
}

function PositionPage() {
  const [records, setRecords] = useState([]);
  const [recordsUnfiltered, setRecordsUnfiltered] = useState([]);
  // const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);
  const [postUserOptions, setPostUserOptions] = useState([]);
  const [licenseClassOptions, setLicenseClassOptions] = useState([]);
  const [licenseConditionOptions, setLicenseConditionOptions] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [jobTemplates, setJobTemplates] = useState([]);
  const [positionGroups, setPositionGroups] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState(null);
  const positionTableRef = useRef(null);

  //  Position Status
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);

  const [formError, setFormError] = useState(null);

  const [statusForm, setStatusForm] = useState({
    p_status: 1,
    p_date_posted: "",
    p_closing_date: "",
  });

  //  Access Control (reactive)
  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    positions: {
      create: hasPermission("PositionPage", "Position", "create"),
      edit: hasPermission("PositionPage", "Position", "edit"),
      delete: hasPermission("PositionPage", "Position", "delete"),
    },
  };

  const itemsPerPage = 20;

  const getOrgUnitName = useCallback(
    (record) =>
      orgUnits.find((ou) => ou.ou_id === parseInt(record.p_org_unit))?.ou_name ||
      record.p_org_unit,
    [orgUnits],
  );

  const getPositionTypeName = useCallback(
    (record) =>
      positionTypes.find((pt) => pt.pt_id === parseInt(record.p_position_type))
        ?.pt_name || record.p_position_type,
    [positionTypes],
  );

  const sortedRecords = useMemo(() => {
    if (!sortConfig) return records;

    const accessors = [
      (record) => record.p_position_number,
      (record) => record.p_position_title,
      getOrgUnitName,
      getPositionTypeName,
      (record) => record.p_contact_info?.city || "-",
      (record) => record.p_posting?.p_closing_date || "-",
      (record) => (record.p_status === 1 ? "Open" : "Closed"),
    ];
    const getValue = accessors[sortConfig.columnIndex];
    if (!getValue) return records;

    return [...records].sort((left, right) => {
      const leftValue = normalizeSortValue(getValue(left));
      const rightValue = normalizeSortValue(getValue(right));

      if (leftValue < rightValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (leftValue > rightValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [getOrgUnitName, getPositionTypeName, records, sortConfig]);

  const paginatedRecords = sortedRecords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    const table = positionTableRef.current;
    if (!table) return undefined;

    const handleClientSort = (event) => {
      setCurrentPage(1);
      setSortConfig({
        columnIndex: event.detail.columnIndex,
        direction: event.detail.direction,
      });
    };

    table.addEventListener("nw-client-sort", handleClientSort);
    return () => table.removeEventListener("nw-client-sort", handleClientSort);
  });

  // Options (can come from DB if needed)
  const travelOptions = [
    { value: "Canada", label: "Canada" },
    { value: "US", label: "US" },
    { value: "Regional", label: "Regional" },
  ];

  const freightOptions = [
    { value: "Auto Parts", label: "Auto Parts" },
    { value: "Car Carrier", label: "Car Carrier" },
    { value: "Containers", label: "Containers" },
    { value: "Dump", label: "Dump" },
    { value: "Flat Bed", label: "Flat Bed" },
    { value: "Hazmat", label: "Hazmat" },
    { value: "Heavy/Oversize", label: "Heavy/Oversize" },
    { value: "Other", label: "Other" },
    { value: "Reefer", label: "Reefer" },
    { value: "Tank", label: "Tank" },
    { value: "Van", label: "Van" },
  ];

  const defaultForm = {
    p_position_title: "",
    p_position_number: "",
    p_category: "",
    p_sub_category: "",
    p_org_unit: "",
    p_position_type: "",
    p_shifts: [],
    p_posting: {
      p_date_posted: dayjs().format("YYYY-MM-DD"),
      p_created_by: "",
      p_closing_date: dayjs().format("YYYY-MM-DD"),
      p_send_posting_to: currentUserId || "",
      p_override_repost: false,
      p_send_group: [],
      p_posted_by: currentUserId || "",
      p_reason_for_replacement: "",
      p_num_openings: "",
      p_reason_for_new_hire: "",
      p_notify_mode: "Recruiter",
      p_notify_to: currentUserId || "",
    },

    p_visible_info: {
      dangerous_goods: false,
      license_classes: false,
      openings: false,
      work_type: false,
      route: false,
      closing_date: false,
    },
    p_landing_page_sell: {
      show_on_landing_pages: ["careers"],
      license_class: [],
      license_conditions: [],
      driver_type: "",
      dangerous_goods: "",
      position_type: "Full Time",
      canada_us_dot: "",
    },
    p_landing_page_desc: "",
    p_internal_desc: "",
    p_comments: "",
    p_reqs: {
      p_start_date: "",
      p_home_time_policy: "",
      p_travel_areas: "",
      p_freight_types: "",
      p_length_of_run: "",
      p_dedicated_truck: "",
      p_dedicated_run: "",
      p_open_board: "",
      p_trip_envelopes: "",
      p_trip_drop: "",
      p_parking_req: "",
    },
    p_contact_info: {
      name: "",
      title: "",
      toll_free: "",
      fax: "",
      local: "",
      email: "",
      address: "",
      address2: "",
      postal: "",
      city: "",
      province: "",
      country: "",
    },
    p_hiring_area: [""],
    p_report_to: {
      name: "",
      title: "",
      toll_free: "",
      fax: "",
      local: "",
      email: "",
      address: "",
      postal: "",
      city: "",
      province: "",
      country: "",
    },
    p_pay_rates: {
      mileage_rate: "",
      waiting_time_rate: "",
      layover_rate: "",
      breakdown_rate: "",
      tarp_rate: "",
      pick_rate: "",
      drop_rate: "",
      load_rate: "",
      unload_rate: "",
      border_crossing_rate: "",
      hourly: "",
      gross_earnings: "",
      earnings_freq: "Weekly",
      hours_per_day: "",
      waiting_policy: "",
      layover_policy: "",
      avg_miles_per_trip: "",
    },
    p_driver_reqs: {
      demerit: "",
      merit: "",
      cvor: "",
      years_exp: "",
      fast_card: "",
    },
    p_orientation: {
      orientation: "",
      hotel: "",
      paid: "",
      length: "1-2 days",
      province: "",
    },
    p_division_policies: {
      mentoring: "",
      pets: "",
      riders: "",
      contractors: "",
    },
  };
  const [form, setForm] = useState(defaultForm);

  const selectedCategoryID = form.p_category;

  const skipRequirements =
    selectedCategoryID === 1 ||
    selectedCategoryID === 5 ||
    selectedCategoryID === 7;

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const res = await axios.get(`${BASE_URL}/api/positions`, { headers });
      setRecordsUnfiltered(res.data);
      setRecords(res.data);

      const [
        catRes,
        subCatRes,
        orgRes,
        typeRes,
        puRes,
        classesRes,
        condsRes,
        groupRes,
        jobDescRes,
      ] = await Promise.all([
        axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/position-types`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/posting-users`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/license-classes`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/license-conditions`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/position-groups`, { headers }),
        axios.get(`${BASE_URL}/api/users?emp_category=Office Staff`, {
          headers,
        }),
        axios.get(`${BASE_URL}/api/job-desc/list`, { headers }),
      ]);
      setCategories(catRes.data);
      setSubCategories(subCatRes.data);
      setOrgUnits(orgRes.data);
      setPositionTypes(typeRes.data);
      setPostUserOptions(puRes.data);
      setLicenseClassOptions(
        classesRes.data.map((c) => ({
          value: c.class_code,
          label: c.class_code,
        })),
      );

      setLicenseConditionOptions(
        condsRes.data.map((c) => ({
          value: c.condition_code,
          label: c.condition_code,
        })),
      );
      setJobTemplates(jobDescRes.data);
      setPositionGroups(groupRes.data);
    } catch (err) {
      console.error("❌ Fetch error", err);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchNextPositionNumber = async () => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    const res = await axios.get(`${BASE_URL}/api/positions/next-position-number`, {
      headers,
    });
    return res.data?.nextPositionNumber || "";
  };

  const openDrawer = async (rec = null) => {
    const merged = rec
      ? {
          ...defaultForm,
          ...rec,
          p_posting: { ...defaultForm.p_posting, ...(rec.p_posting || {}) },
          p_visible_info: {
            ...defaultForm.p_visible_info,
            ...(rec.p_visible_info || {}),
          },
          p_landing_page_sell: {
            ...defaultForm.p_landing_page_sell,
            ...(rec.p_landing_page_sell || {}),
          },
          p_reqs: { ...defaultForm.p_reqs, ...(rec.p_reqs || {}) },
          p_contact_info: {
            ...defaultForm.p_contact_info,
            ...(rec.p_contact_info || {}),
          },
          p_hiring_area: rec.p_hiring_area || defaultForm.p_hiring_area,
          p_report_to: {
            ...defaultForm.p_report_to,
            ...(rec.p_report_to || {}),
          },
          p_pay_rates: {
            ...defaultForm.p_pay_rates,
            ...(rec.p_pay_rates || {}),
          },
          p_driver_reqs: {
            ...defaultForm.p_driver_reqs,
            ...(rec.p_driver_reqs || {}),
          },
          p_orientation: {
            ...defaultForm.p_orientation,
            ...(rec.p_orientation || {}),
          },
          p_division_policies: {
            ...defaultForm.p_division_policies,
            ...(rec.p_division_policies || {}),
          },
        }
      : { ...defaultForm };

    // 🧩 Normalize landing page arrays (this prevents the .map error)
    const lp = merged.p_landing_page_sell;
    merged.p_landing_page_sell = {
      ...lp,
      license_class: Array.isArray(lp.license_class)
        ? lp.license_class
        : typeof lp.license_class === "string" && lp.license_class.trim() !== ""
          ? [lp.license_class]
          : [],
      license_conditions: Array.isArray(lp.license_conditions)
        ? lp.license_conditions
        : typeof lp.license_conditions === "string" &&
            lp.license_conditions.trim() !== ""
          ? [lp.license_conditions]
          : [],
    };

    // Derive mode if missing on existing records
    const hasArrayNotify = Array.isArray(merged.p_posting?.p_notify_to);
    const derivedMode =
      merged.p_posting?.p_notify_mode ||
      (hasArrayNotify ? "Group" : "Recruiter");
    merged.p_posting = { ...merged.p_posting, p_notify_mode: derivedMode };

    if (!rec) {
      try {
        merged.p_position_number = await fetchNextPositionNumber();
      } catch (err) {
        console.error("❌ Failed to fetch next position number", err);
      }
    }

    setEditRec(rec);
    setFormError(null);
    setForm(merged);
    setShowDrawer(true);
  };

  const handleChange = (key, value, parentKey = null) => {
    if (parentKey) {
      setForm((prev) => ({
        ...prev,
        [parentKey]: { ...prev[parentKey], [key]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [key]: value }));
    }
  };

  const toggleShift = (shift) => {
    const arr = form.p_shifts.includes(shift)
      ? form.p_shifts.filter((s) => s !== shift)
      : [...form.p_shifts, shift];
    handleChange("p_shifts", arr);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    const payload = { ...form };

    try {
      if (editRec) {
        await axios.put(`${BASE_URL}/api/positions/${editRec.p_id}`, payload, {
          headers,
        });
      } else {
        await axios.post(`${BASE_URL}/api/positions`, payload, { headers });
      }

      setShowDrawer(false);
      fetchData();
    } catch (err) {
      if (err.response?.status === 409) {
        const msg =
          err.response.data.message ||
          "Position number already exists in the system.";

        // Inline error (keeps red field)
        setFormError(msg);

        // 🔔 Native browser popup
        window.alert(msg);

        return;
      }

      console.error("❌ Save error", err);
      window.alert("Something went wrong while saving the position.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Confirm delete?")) return;

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.delete(`${BASE_URL}/api/positions/${id}`, { headers });
      alert("✅ Position deleted successfully");
      fetchData();
    } catch (err) {
      console.error("❌ Delete error", err);
      alert("Failed to delete position");
    }
  };

  useEffect(() => {
    const query = searchQuery.toLowerCase();

    const filtered = recordsUnfiltered.filter((item) => {
      const orgUnitName =
        orgUnits.find((ou) => ou.ou_id === parseInt(item.p_org_unit))
          ?.ou_name || "";
      const typeName =
        positionTypes.find((pt) => pt.pt_id === parseInt(item.p_position_type))
          ?.pt_name || "";
      const contactCity = item.p_contact_info?.city || "";

      return (
        item.p_position_number?.toString().toLowerCase().includes(query) ||
        item.p_position_title?.toLowerCase().includes(query) ||
        orgUnitName.toLowerCase().includes(query) ||
        typeName.toLowerCase().includes(query) ||
        contactCity.toLowerCase().includes(query) ||
        (item.p_status === 1 ? "open" : "closed").includes(query)
      );
    });

    setRecords(filtered);
  }, [searchQuery, recordsUnfiltered, orgUnits, positionTypes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const selectedOU = orgUnits.find(
    (ou) => ou.ou_id === parseInt(form.p_org_unit),
  );
  const selectedCategory = categories.find(
    (c) => c.c_id === parseInt(form.p_category),
  );
  const selectedSubCategory = subCategories.find(
    (sc) => sc.sc_id === parseInt(form.p_sub_category),
  );
  const selectedPT = positionTypes.find(
    (pt) => pt.pt_id === parseInt(form.p_position_type),
  );

  return (
    <Container className="form-wrapper my-4">
      <div className="text-center mb-4">
        <h3>Positions</h3>
      </div>

      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title className="mb-0">All Positions</Card.Title>
            {PERMISSIONS.positions.create && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => openDrawer(null)}
              >
                Add Position
              </Button>
            )}
          </div>

          <div className="row mb-3">
            <div className="col">
              <input
                type="text"
                className="form-control"
                placeholder="Search by any field..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {records.length === 0 ? (
            <p className="mt-3 text-muted fst-italic">No positions found.</p>
          ) : (
            <div className="table-responsive">
              <table
                ref={positionTableRef}
                className="table table-bordered align-middle position-table"
                data-nw-client-sort="true"
              >
                <thead className="table-light">
                  <tr>
                    <th>NUMBER</th>
                    <th>NAME</th>
                    <th>ORG UNIT</th>
                    <th>TYPE</th>
                    <th>LOCATION</th>
                    <th>Close Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((r, idx) => {
                    if (!r.p_id) console.warn("⚠️ Missing p_id on record:", r);
                    return (
                      <tr
                        key={r.p_id || `position-${idx}`}
                        className="position-row"
                        onClick={(e) => {
                          if (!e.target.closest(".action-cell")) {
                            setSelectedRecord(r);
                          }
                        }}
                      >
                        <td>{r.p_position_number}</td>
                        <td>{r.p_position_title}</td>
                        <td>
                          {getOrgUnitName(r)}
                        </td>
                        <td>{getPositionTypeName(r)}</td>
                        <td>{r.p_contact_info?.city || "-"}</td>
                        <td>{r.p_posting?.p_closing_date || "-"}</td>
                        <td
                          style={{ cursor: "pointer", fontWeight: 600 }}
                          onClick={(e) => {
                            e.stopPropagation(); // 🚫 keep row click behavior intact

                            setStatusTarget(r);
                            setStatusForm({
                              p_status: r.p_status,
                              p_date_posted: r.p_posting?.p_date_posted || "",
                              p_closing_date: r.p_posting?.p_closing_date || "",
                            });

                            setShowStatusModal(true);
                          }}
                        >
                          {r.p_status === 1 ? "Open" : "Closed"}
                        </td>

                        <td className="action-cell">
                          <div className="d-flex gap-2 justify-content-center">
                            {/* ✏️ Edit button */}
                            <button
                              className="btn btn-warning btn-sm btn-action"
                              onClick={() => openDrawer(r)}
                              disabled={!PERMISSIONS.positions.edit}
                            >
                              <FaEdit />
                            </button>

                            {/* 🗑️ Delete button */}
                            {PERMISSIONS.positions.delete && (
                              <button
                                className="btn btn-danger btn-sm btn-action"
                                onClick={() => handleDelete(r.p_id)}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination
                currentPage={currentPage}
                totalItems={records.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </Card.Body>
      </Card>

      <div className="position-page-container">
        <div className="position-table-section">
          {/* All your table and controls go here */}
        </div>

        {selectedRecord && (
          <div className="position-slide-wrapper show">
            <PositionPanel
              data={selectedRecord}
              onClose={() => setSelectedRecord(null)}
              postUserOptions={postUserOptions}
              categories={categories}
              orgUnits={orgUnits}
              positionTypes={positionTypes}
              onPositionsUpdate={fetchData}
            />
          </div>
        )}
      </div>
      <Offcanvas
        show={showDrawer}
        onHide={() => {
          const confirmed = window.confirm(
            "Are you sure you want to cancel? Unsaved changes will be lost.",
          );
          if (confirmed) {
            setShowDrawer(false);
          }
        }}
        placement="end"
        className="offcanvas-wide"
        backdrop="static"
        keyboard={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {editRec ? "Edit Position" : "New Position"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ height: "90vh", overflowY: "auto" }}>
          <Form onSubmit={handleSave}>
            {/* --- Position Details --- */}
            <Card className="mb-3">
              <Card.Header>Position Details</Card.Header>
              <Card.Body>
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Position Title</Form.Label>
                      <Form.Control
                        value={form.p_position_title}
                        onChange={(e) =>
                          handleChange("p_position_title", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Position #</Form.Label>
                      <Form.Control
                        value={form.p_position_number}
                        onChange={(e) => {
                          setFormError(null);
                          handleChange("p_position_number", e.target.value);
                        }}
                        isInvalid={!!formError}
                        readOnly={!editRec}
                        required
                      />
                      {formError && (
                        <Form.Control.Feedback type="invalid">
                          {formError}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        value={form.p_status ?? 1}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            p_status: Number(e.target.value),
                          }))
                        }
                      >
                        <option value={1}>Open</option>
                        <option value={0}>Closed</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Label>Category</Form.Label>
                    <Select
                      options={categories.map((c) => ({
                        label: c.c_name,
                        value: c.c_id,
                      }))}
                      value={
                        selectedCategory
                          ? {
                              label: selectedCategory.c_name,
                              value: selectedCategory.c_id,
                            }
                          : null
                      }
                      onChange={(sel) =>
                        handleChange("p_category", sel?.value || "")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Org Unit</Form.Label>
                    <Select
                      options={orgUnits.map((u) => ({
                        label: u.ou_name,
                        value: u.ou_id,
                      }))}
                      value={
                        selectedOU
                          ? {
                              label: selectedOU.ou_name,
                              value: selectedOU.ou_id,
                            }
                          : null
                      }
                      onChange={(sel) =>
                        handleChange("p_org_unit", sel?.value || "")
                      }
                    />
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Label>Sub-Category</Form.Label>
                    <Select
                      options={subCategories.map((sc) => ({
                        label: sc.sc_name,
                        value: sc.sc_id,
                      }))}
                      value={
                        selectedSubCategory
                          ? {
                              label: selectedSubCategory.sc_name,
                              value: selectedSubCategory.sc_id,
                            }
                          : null
                      }
                      onChange={(sel) =>
                        handleChange("p_sub_category", sel?.value || "")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Position Type</Form.Label>
                    <Select
                      options={positionTypes.map((p) => ({
                        label: p.pt_name,
                        value: p.pt_id,
                      }))}
                      value={
                        selectedPT
                          ? {
                              label: selectedPT.pt_name,
                              value: selectedPT.pt_id,
                            }
                          : null
                      }
                      onChange={(sel) =>
                        handleChange("p_position_type", sel?.value || "")
                      }
                    />
                  </Col>
                </Row>
                <Form.Label className="my-3">Shift(s)</Form.Label>
                <Row>
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                    "Day",
                    "Night",
                    "Evening",
                    "Spare Board",
                    "Operates On Holidays",
                  ].map((sh) => (
                    <Col md={4} key={sh}>
                      <Form.Check
                        label={sh}
                        checked={form.p_shifts.includes(sh)}
                        onChange={() => toggleShift(sh)}
                      />
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>

            {/* --- Posting Details --- */}
            <Card className="mb-3">
              <Card.Header>Posting Details</Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Date Posted</Form.Label>
                      <Form.Control
                        type="date"
                        value={form.p_posting?.p_date_posted || ""}
                        onChange={(e) =>
                          handleChange("p_posting", {
                            ...form.p_posting,
                            p_date_posted: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Created By</Form.Label>
                    <Select
                      options={postUserOptions}
                      isClearable
                      value={postUserOptions.find(
                        (u) => u.value === form.p_posting?.p_created_by,
                      )}
                      onChange={(sel) =>
                        handleChange("p_posting", {
                          ...form.p_posting,
                          p_created_by: sel?.value || "",
                        })
                      }
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Closing Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={form.p_posting?.p_closing_date || ""}
                        onChange={(e) =>
                          handleChange("p_posting", {
                            ...form.p_posting,
                            p_closing_date: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Send Posting To</Form.Label>
                    <Select
                      options={postUserOptions}
                      isClearable
                      value={postUserOptions.find(
                        (u) => u.value === form.p_posting?.p_send_posting_to,
                      )}
                      onChange={(sel) => {
                        const newRecruiterId = sel?.value || "";
                        const isRecruiterMode =
                          form.p_posting?.p_notify_mode === "Recruiter";
                        handleChange("p_posting", {
                          ...form.p_posting,
                          p_send_posting_to: newRecruiterId,
                          p_notify_to: isRecruiterMode
                            ? newRecruiterId
                            : form.p_posting?.p_notify_to,
                        });
                      }}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Override Global Re-Post Setting</Form.Label>
                      <div>
                        <Form.Check
                          inline
                          label="Yes"
                          type="radio"
                          checked={form.p_posting?.p_override_repost === true}
                          onChange={() =>
                            handleChange("p_posting", {
                              ...form.p_posting,
                              p_override_repost: true,
                            })
                          }
                        />
                        <Form.Check
                          inline
                          label="No"
                          type="radio"
                          checked={form.p_posting?.p_override_repost === false}
                          onChange={() =>
                            handleChange("p_posting", {
                              ...form.p_posting,
                              p_override_repost: false,
                            })
                          }
                        />
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Send to Group(s)</Form.Label>
                      <Select
                        isMulti
                        fullWidth
                        options={positionGroups.map((g) => ({
                          label: g.pg_name,
                          value: g.pg_id,
                        }))}
                        value={(Array.isArray(form.p_posting?.p_send_group)
                          ? form.p_posting.p_send_group
                          : (() => {
                              try {
                                return JSON.parse(
                                  form.p_posting?.p_send_group || "[]",
                                );
                              } catch {
                                return [];
                              }
                            })()
                        )
                          .map((id) => {
                            const g = positionGroups.find(
                              (g) => g.pg_id === id,
                            );
                            return g
                              ? { label: g.pg_name, value: g.pg_id }
                              : null;
                          })
                          .filter(Boolean)}
                        getOptionLabel={(e) => e.label}
                        getOptionValue={(e) => e.value}
                        onChange={(selected) => {
                          const ids = (selected || []).map((opt) => opt.value);
                          const isGroupMode =
                            form.p_posting?.p_notify_mode === "Group";
                          handleChange("p_posting", {
                            ...form.p_posting,
                            p_send_group: ids,
                            p_notify_to: isGroupMode
                              ? ids
                              : form.p_posting?.p_notify_to,
                          });
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>Posted By</Form.Label>
                    <Select
                      options={postUserOptions}
                      isClearable
                      value={
                        postUserOptions.find(
                          (u) => u.value === form.p_posting?.p_posted_by,
                        ) || null
                      }
                      onChange={(sel) =>
                        handleChange("p_posting", {
                          ...form.p_posting,
                          p_posted_by: sel?.value ?? "",
                        })
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Reason for Replacement</Form.Label>
                      <Select
                        options={[
                          { value: "Bad Equipment", label: "Bad Equipment" },
                          { value: "Driver Manager", label: "Driver Manager" },
                          { value: "Family Issue", label: "Family Issue" },
                          { value: "Low Mileage", label: "Low Mileage" },
                          { value: "Low Rate", label: "Low Rate" },
                          { value: "Medical Issue", label: "Medical Issue" },
                          {
                            value: "Not enough hours",
                            label: "Not enough hours",
                          },
                          { value: "Other", label: "Other" },
                          {
                            value: "Poor Performance",
                            label: "Poor Performance",
                          },
                          { value: "Retirement", label: "Retirement" },
                          { value: "Safety Related", label: "Safety Related" },
                        ]}
                        isClearable
                        value={
                          form.p_posting?.p_reason_for_replacement
                            ? {
                                value: form.p_posting.p_reason_for_replacement,
                                label: form.p_posting.p_reason_for_replacement,
                              }
                            : null
                        }
                        onChange={(sel) =>
                          handleChange("p_posting", {
                            ...form.p_posting,
                            p_reason_for_replacement: sel?.value || "",
                          })
                        }
                        placeholder="Select reason..."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>No. of Openings</Form.Label>
                      <Form.Control
                        type="number"
                        value={form.p_posting?.p_num_openings || ""}
                        onChange={(e) =>
                          handleChange("p_posting", {
                            ...form.p_posting,
                            p_num_openings: +e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Reason for New Hire</Form.Label>
                      <Select
                        options={[
                          { value: "New Business", label: "New Business" },
                          { value: "Other", label: "Other" },
                          { value: "Restructure", label: "Restructure" },
                        ]}
                        isClearable
                        value={
                          form.p_posting?.p_reason_for_new_hire
                            ? {
                                value: form.p_posting.p_reason_for_new_hire,
                                label: form.p_posting.p_reason_for_new_hire,
                              }
                            : null
                        }
                        onChange={(sel) =>
                          handleChange("p_posting", {
                            ...form.p_posting,
                            p_reason_for_new_hire: sel?.value || "",
                          })
                        }
                        placeholder="Select reason..."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Send notifications to</Form.Label>
                      <Select
                        options={[
                          { label: "Recruiter", value: "Recruiter" },
                          { label: "Group", value: "Group" },
                        ]}
                        isClearable
                        value={
                          form.p_posting?.p_notify_mode
                            ? {
                                label: form.p_posting.p_notify_mode,
                                value: form.p_posting.p_notify_mode,
                              }
                            : null
                        }
                        onChange={(sel) => {
                          const mode = sel?.value || "Recruiter";
                          const nextNotifyTo =
                            mode === "Group"
                              ? Array.isArray(form.p_posting?.p_send_group)
                                ? form.p_posting.p_send_group
                                : []
                              : form.p_posting?.p_send_posting_to || "";

                          handleChange("p_posting", {
                            ...form.p_posting,
                            p_notify_mode: mode,
                            p_notify_to: nextNotifyTo,
                          });
                        }}
                        placeholder="Select notification target..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Visible Information */}
            <Card className="mb-3">
              <Card.Header>Visible Information</Card.Header>
              <Card.Body>
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Label>Dangerous Goods / Hazmat</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="Show"
                        checked={form.p_visible_info.dangerous_goods === true}
                        onChange={() =>
                          handleChange(
                            "dangerous_goods",
                            true,
                            "p_visible_info",
                          )
                        }
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Hide"
                        checked={form.p_visible_info.dangerous_goods === false}
                        onChange={() =>
                          handleChange(
                            "dangerous_goods",
                            false,
                            "p_visible_info",
                          )
                        }
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Label>License Classes</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="Show"
                        checked={form.p_visible_info.license_classes === true}
                        onChange={() =>
                          handleChange(
                            "license_classes",
                            true,
                            "p_visible_info",
                          )
                        }
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Hide"
                        checked={form.p_visible_info.license_classes === false}
                        onChange={() =>
                          handleChange(
                            "license_classes",
                            false,
                            "p_visible_info",
                          )
                        }
                      />
                    </div>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Label>Openings</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="Show"
                        checked={form.p_visible_info.openings === true}
                        onChange={() =>
                          handleChange("openings", true, "p_visible_info")
                        }
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Hide"
                        checked={form.p_visible_info.openings === false}
                        onChange={() =>
                          handleChange("openings", false, "p_visible_info")
                        }
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Work Type</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="Show"
                        checked={form.p_visible_info.work_type === true}
                        onChange={() =>
                          handleChange("work_type", true, "p_visible_info")
                        }
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Hide"
                        checked={form.p_visible_info.work_type === false}
                        onChange={() =>
                          handleChange("work_type", false, "p_visible_info")
                        }
                      />
                    </div>
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Label>Route</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="Show"
                        checked={form.p_visible_info.route === true}
                        onChange={() =>
                          handleChange("route", true, "p_visible_info")
                        }
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Hide"
                        checked={form.p_visible_info.route === false}
                        onChange={() =>
                          handleChange("route", false, "p_visible_info")
                        }
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Closing Date</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="Show"
                        checked={form.p_visible_info.closing_date === true}
                        onChange={() =>
                          handleChange("closing_date", true, "p_visible_info")
                        }
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="Hide"
                        checked={form.p_visible_info.closing_date === false}
                        onChange={() =>
                          handleChange("closing_date", false, "p_visible_info")
                        }
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Landing Page Sell</Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  {/* <Col md={6}>
                    <Form.Group>
                      <Form.Label>Show on landing page(s)</Form.Label>
                      <Select
                        isMulti
                        value={form.p_landing_page_sell.show_on_landing_pages.map(
                          (page) => ({
                            label: page,
                            value: page,
                          })
                        )}
                        options={[
                          { label: "careers", value: "careers" },
                          { label: "indeed", value: "indeed" },
                          { label: "website", value: "website" },
                        ]}
                        onChange={(selected) =>
                          handleChange(
                            "show_on_landing_pages",
                            selected.map((s) => s.value),
                            "p_landing_page_sell"
                          )
                        }
                      />
                    </Form.Group>
                  </Col> */}
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>This position is</Form.Label>
                      <Select
                        value={{
                          label: form.p_landing_page_sell.position_type,
                          value: form.p_landing_page_sell.position_type,
                        }}
                        isClearable
                        options={[
                          { label: "Full Time", value: "Full Time" },
                          { label: "Part Time", value: "Part Time" },
                          { label: "Contract", value: "Contract" },
                        ]}
                        onChange={(sel) =>
                          handleChange(
                            "position_type",
                            sel.value,
                            "p_landing_page_sell",
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {!skipRequirements && (
                  <>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Single or Team Driver</Form.Label>
                          <div>
                            {["Single", "Team", "Single & Team"].map((val) => (
                              <Form.Check
                                inline
                                key={val}
                                type="radio"
                                label={val}
                                name="driver_type"
                                checked={
                                  form.p_landing_page_sell.driver_type === val
                                }
                                onChange={() =>
                                  handleChange(
                                    "driver_type",
                                    val,
                                    "p_landing_page_sell",
                                  )
                                }
                              />
                            ))}
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Licence Class</Form.Label>
                          <Select
                            isMulti
                            options={licenseClassOptions}
                            value={(
                              form.p_landing_page_sell.license_class || []
                            ).map((cls) => ({
                              value: cls,
                              label: cls,
                            }))}
                            onChange={(selected) =>
                              handleChange(
                                "license_class",
                                selected.map((s) => s.value),
                                "p_landing_page_sell",
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Licence Conditions</Form.Label>
                          <Select
                            isMulti
                            options={licenseConditionOptions}
                            value={(
                              form.p_landing_page_sell.license_conditions || []
                            ).map((cond) => ({
                              value: cond,
                              label: cond,
                            }))}
                            onChange={(selected) =>
                              handleChange(
                                "license_conditions",
                                selected.map((s) => s.value),
                                "p_landing_page_sell",
                              )
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Canada-US (DOT)</Form.Label>
                      <div>
                        {["Yes", "No"].map((val) => (
                          <Form.Check
                            inline
                            key={val}
                            type="radio"
                            label={val}
                            name="canada_us_dot"
                            checked={
                              form.p_landing_page_sell.canada_us_dot === val
                            }
                            onChange={() =>
                              handleChange(
                                "canada_us_dot",
                                val,
                                "p_landing_page_sell",
                              )
                            }
                          />
                        ))}
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Dangerous Goods / Hazmat</Form.Label>
                      <div>
                        {["Yes", "No"].map((val) => (
                          <Form.Check
                            inline
                            key={val}
                            type="radio"
                            label={val}
                            name="hazmat"
                            checked={
                              form.p_landing_page_sell.dangerous_goods === val
                            }
                            onChange={() =>
                              handleChange(
                                "dangerous_goods",
                                val,
                                "p_landing_page_sell",
                              )
                            }
                          />
                        ))}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* <Row className="mb-3">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Landing Page Description</Form.Label>
                                            <ReactQuill
                                                value={form.p_landing_page_desc}
                                                onChange={(val) => handleChange('p_landing_page_desc', val)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row> */}
              </Card.Body>
            </Card>

            {/* --- Landing Page Description --- */}
            <Card className="mb-3">
              <Card.Header>Landing Page Description</Card.Header>
              <Card.Body>
                <Form.Text className="text-muted mb-2 d-block">
                  These position details get posted to external job boards.
                </Form.Text>
                <ReactQuill
                  value={form.p_landing_page_desc}
                  onChange={(val) => handleChange("p_landing_page_desc", val)}
                />
              </Card.Body>
            </Card>

            {/* --- Internal Position/Job Description --- */}
            <Card className="mb-3">
              <Card.Header>Position/Job Description</Card.Header>
              <Card.Body>
                <Form.Text className="text-muted mb-2 d-block">
                  This description is for internal use only and does not get
                  posted to external job boards or display on the landing page.
                </Form.Text>

                {/* Dropdown for selecting job description templates */}
                <Form.Group className="mb-3">
                  <Form.Label>Select Job Descriptions</Form.Label>
                  <Form.Select
                    onChange={async (e) => {
                      const jdId = e.target.value;
                      if (!jdId) return;

                      const token = localStorage.getItem("token");
                      const headers = { Authorization: `Bearer ${token}` };

                      try {
                        const res = await axios.get(
                          `${BASE_URL}/api/job-desc/${jdId}`,
                          { headers },
                        );
                        handleChange("p_internal_desc", res.data.jd_body); // Insert body
                      } catch (err) {
                        console.error(
                          "❌ Failed to load job description:",
                          err,
                        );
                      }
                    }}
                  >
                    <option value="">-- Select a template --</option>
                    {jobTemplates.map((t, idx) => (
                      <option key={t.jd_id || idx} value={t.jd_id}>
                        {t.jd_title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <ReactQuill
                  value={form.p_internal_desc}
                  onChange={(val) => handleChange("p_internal_desc", val)}
                />
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Comments / Special Instructions</Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={form.p_comments}
                    onChange={(e) => handleChange("p_comments", e.target.value)}
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Requirements</Card.Header>
              <Card.Body>
                <Row className="mb-4">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={form.p_reqs?.p_start_date || ""}
                        onChange={(e) =>
                          handleChange("p_reqs", {
                            ...form.p_reqs,
                            p_start_date: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Home Time Policy</Form.Label>
                      <Form.Select
                        value={form.p_reqs?.p_home_time_policy || ""}
                        onChange={(e) =>
                          handleChange("p_reqs", {
                            ...form.p_reqs,
                            p_home_time_policy: e.target.value,
                          })
                        }
                      >
                        <option value="">-- Select Policy --</option>
                        <option value="5 days on 2 days off">
                          5 days on 2 days off
                        </option>
                        <option value="6 days on 1 day off">
                          6 days on 1 day off
                        </option>
                        <option value="7 days on 1 day off">
                          7 days on 1 day off
                        </option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {!skipRequirements && (
                  <>
                    <Row className="mb-2">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Areas Of Travel</Form.Label>
                          <Select
                            isMulti
                            options={travelOptions}
                            value={(form.p_reqs?.p_travel_areas || []).map(
                              (v) => ({
                                value: v,
                                label: v,
                              }),
                            )}
                            onChange={(selected) =>
                              handleChange("p_reqs", {
                                ...form.p_reqs,
                                p_travel_areas: selected.map((s) => s.value),
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Freight Types</Form.Label>
                          <Select
                            isMulti
                            options={freightOptions}
                            value={(form.p_reqs?.p_freight_types || []).map(
                              (v) => ({ value: v, label: v }),
                            )}
                            onChange={(selected) =>
                              handleChange("p_reqs", {
                                ...form.p_reqs,
                                p_freight_types: selected.map((s) => s.value),
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Length Of Run</Form.Label>
                          <Form.Control
                            value={form.p_reqs?.p_length_of_run || ""}
                            onChange={(e) =>
                              handleChange("p_reqs", {
                                ...form.p_reqs,
                                p_length_of_run: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}

                {!skipRequirements && (
                  <>
                    {[
                      { key: "p_dedicated_truck", label: "Dedicated Trucks" },
                      { key: "p_dedicated_run", label: "Dedicated Run" },
                      { key: "p_open_board", label: "Open Board" },
                      {
                        key: "p_trip_envelopes",
                        label: "Trip Envelopes Required",
                      },
                    ].map(({ key, label }) => (
                      <Row className="mb-3 mt-4" key={key}>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>{label}</Form.Label>
                            <div>
                              <Form.Check
                                inline
                                label="Yes"
                                type="radio"
                                checked={form.p_reqs?.[key] === true}
                                onChange={() =>
                                  handleChange("p_reqs", {
                                    ...form.p_reqs,
                                    [key]: true,
                                  })
                                }
                              />
                              <Form.Check
                                inline
                                label="No"
                                type="radio"
                                checked={form.p_reqs?.[key] === false}
                                onChange={() =>
                                  handleChange("p_reqs", {
                                    ...form.p_reqs,
                                    [key]: false,
                                  })
                                }
                              />
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    ))}

                    <Row>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Trip Sheet Drop Locations</Form.Label>
                          <Form.Control
                            value={form.p_reqs?.p_trip_drop || ""}
                            onChange={(e) =>
                              handleChange("p_reqs", {
                                ...form.p_reqs,
                                p_trip_drop: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Parking Requirements</Form.Label>
                          <Form.Control
                            value={form.p_reqs?.p_parking_req || ""}
                            onChange={(e) =>
                              handleChange("p_reqs", {
                                ...form.p_reqs,
                                p_parking_req: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Contact Information</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Label>Contact Name</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.name}
                      onChange={(e) =>
                        handleChange("name", e.target.value, "p_contact_info")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Address 1</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.address}
                      onChange={(e) =>
                        handleChange(
                          "address",
                          e.target.value,
                          "p_contact_info",
                        )
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>Contact Title</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.title}
                      onChange={(e) =>
                        handleChange("title", e.target.value, "p_contact_info")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Address 2</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.address2}
                      onChange={(e) =>
                        handleChange(
                          "address2",
                          e.target.value,
                          "p_contact_info",
                        )
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>Toll-Free Phone</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.toll_free}
                      onChange={(e) =>
                        handleChange(
                          "toll_free",
                          e.target.value,
                          "p_contact_info",
                        )
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Postal Code</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.postal}
                      onChange={(e) =>
                        handleChange("postal", e.target.value, "p_contact_info")
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Label>Fax</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.fax}
                      onChange={(e) =>
                        handleChange("fax", e.target.value, "p_contact_info")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.city}
                      onChange={(e) =>
                        handleChange("city", e.target.value, "p_contact_info")
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>Local Phone</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.local}
                      onChange={(e) =>
                        handleChange("local", e.target.value, "p_contact_info")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Province</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.province}
                      onChange={(e) =>
                        handleChange(
                          "province",
                          e.target.value,
                          "p_contact_info",
                        )
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>Contact Email</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.email}
                      onChange={(e) =>
                        handleChange("email", e.target.value, "p_contact_info")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Country</Form.Label>
                    <Form.Control
                      value={form.p_contact_info.country}
                      onChange={(e) =>
                        handleChange(
                          "country",
                          e.target.value,
                          "p_contact_info",
                        )
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Label>Province-wide Job Posting</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        checked={form.p_contact_info.province_wide === true}
                        onChange={() =>
                          handleChange("province_wide", true, "p_contact_info")
                        }
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        checked={form.p_contact_info.province_wide === false}
                        onChange={() =>
                          handleChange("province_wide", false, "p_contact_info")
                        }
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {form.p_contact_info.province_wide === false && (
              <Card className="mb-3">
                <Card.Header>Hiring Area</Card.Header>
                <Card.Body>
                  <Form.Text className="text-muted mb-2 d-block">
                    Please add one or more hiring areas for this requisition.
                    These will be passed to job boards.
                  </Form.Text>

                  {form.p_hiring_area.map((postal, idx) => (
                    <Row className="mb-2" key={idx}>
                      <Col md={10}>
                        <Form.Control
                          value={postal}
                          onChange={(e) => {
                            const newArr = [...form.p_hiring_area];
                            newArr[idx] = e.target.value;
                            handleChange("p_hiring_area", newArr);
                          }}
                        />
                      </Col>
                      <Col md={2}>
                        <Button
                          variant="danger"
                          onClick={() => {
                            const newArr = form.p_hiring_area.filter(
                              (_, i) => i !== idx,
                            );
                            handleChange("p_hiring_area", newArr);
                          }}
                        >
                          Delete
                        </Button>
                      </Col>
                    </Row>
                  ))}

                  <Button
                    onClick={() =>
                      handleChange("p_hiring_area", [...form.p_hiring_area, ""])
                    }
                  >
                    Add
                  </Button>
                </Card.Body>
              </Card>
            )}

            <Card className="mb-3">
              <Card.Header>Report To Information</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Label>Report To Name</Form.Label>
                    <Form.Control
                      value={form.p_report_to.name}
                      onChange={(e) =>
                        handleChange("name", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Address same as Contact</Form.Label>
                    <br />
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          p_report_to: { ...prev.p_contact_info },
                        }))
                      }
                    >
                      Address same as Contact
                    </Button>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>Report To Title</Form.Label>
                    <Form.Control
                      value={form.p_report_to.title}
                      onChange={(e) =>
                        handleChange("title", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Address 1</Form.Label>
                    <Form.Control
                      value={form.p_report_to.address}
                      onChange={(e) =>
                        handleChange("address", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>Toll-Free Phone</Form.Label>
                    <Form.Control
                      value={form.p_report_to.toll_free}
                      onChange={(e) =>
                        handleChange("toll_free", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Address 2</Form.Label>
                    <Form.Control
                      value={form.p_report_to.address2}
                      onChange={(e) =>
                        handleChange("address2", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>Fax</Form.Label>
                    <Form.Control
                      value={form.p_report_to.fax}
                      onChange={(e) =>
                        handleChange("fax", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Postal Code</Form.Label>
                    <Form.Control
                      value={form.p_report_to.postal}
                      onChange={(e) =>
                        handleChange("postal", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>Local Phone</Form.Label>
                    <Form.Control
                      value={form.p_report_to.local}
                      onChange={(e) =>
                        handleChange("local", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      value={form.p_report_to.city}
                      onChange={(e) =>
                        handleChange("city", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>Report to Email</Form.Label>
                    <Form.Control
                      value={form.p_report_to.email}
                      onChange={(e) =>
                        handleChange("email", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Province</Form.Label>
                    <Form.Control
                      value={form.p_report_to.province}
                      onChange={(e) =>
                        handleChange("province", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                </Row>

                <Row className="justify-content-end">
                  <Col md={6}>
                    <Form.Label>Country</Form.Label>
                    <Form.Control
                      style={{ maxWidth: "100%" }}
                      value={form.p_report_to.country}
                      onChange={(e) =>
                        handleChange("country", e.target.value, "p_report_to")
                      }
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Pay Rates (display only)</Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  {/* Left Column */}
                  <Col md={6}>
                    {[
                      { label: "Mileage Rate", key: "mileage_rate" },
                      { label: "Waiting Time Rate", key: "waiting_time_rate" },
                      { label: "Layover Rate", key: "layover_rate" },
                      { label: "Breakdown Rate", key: "breakdown_rate" },
                      { label: "Tarp Rate", key: "tarp_rate" },
                    ].map(({ label, key }) => (
                      <Form.Group className="mb-2" key={key}>
                        <Form.Label>{label}</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            type="number"
                            value={form.p_pay_rates[key]}
                            onChange={(e) =>
                              handleChange(key, e.target.value, "p_pay_rates")
                            }
                          />
                          <Form.Select defaultValue="mile" disabled>
                            <option value="mile">/mile</option>
                          </Form.Select>
                        </InputGroup>
                      </Form.Group>
                    ))}

                    <Form.Group className="mb-2">
                      <Form.Label>Waiting Policy</Form.Label>
                      <Form.Control
                        value={form.p_pay_rates.waiting_policy}
                        onChange={(e) =>
                          handleChange(
                            "waiting_policy",
                            e.target.value,
                            "p_pay_rates",
                          )
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>Layover Policy</Form.Label>
                      <Form.Control
                        value={form.p_pay_rates.layover_policy}
                        onChange={(e) =>
                          handleChange(
                            "layover_policy",
                            e.target.value,
                            "p_pay_rates",
                          )
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>Avg Miles Per Trip</Form.Label>
                      <Form.Control
                        type="number"
                        value={form.p_pay_rates.avg_miles_per_trip}
                        onChange={(e) =>
                          handleChange(
                            "avg_miles_per_trip",
                            e.target.value,
                            "p_pay_rates",
                          )
                        }
                      />
                    </Form.Group>
                  </Col>

                  {/* Right Column */}
                  <Col md={6}>
                    {[
                      { label: "Pick Rate", key: "pick_rate" },
                      { label: "Drop Rate", key: "drop_rate" },
                      { label: "Load Rate", key: "load_rate" },
                      { label: "Unload Rate", key: "unload_rate" },
                      {
                        label: "Border Crossing Rate",
                        key: "border_crossing_rate",
                      },
                    ].map(({ label, key }) => (
                      <Form.Group className="mb-2" key={key}>
                        <Form.Label>{label}</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            type="number"
                            value={form.p_pay_rates[key]}
                            onChange={(e) =>
                              handleChange(key, e.target.value, "p_pay_rates")
                            }
                          />
                          <Form.Select defaultValue="ea" disabled>
                            <option value="ea">/each</option>
                          </Form.Select>
                        </InputGroup>
                      </Form.Group>
                    ))}

                    <Form.Group className="mb-2">
                      <Form.Label>Hourly Rate</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={form.p_pay_rates.hourly}
                          onChange={(e) =>
                            handleChange(
                              "hourly",
                              e.target.value,
                              "p_pay_rates",
                            )
                          }
                        />
                        <InputGroup.Text>/ Hour</InputGroup.Text>
                      </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>Avg. Gross Earnings</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={form.p_pay_rates.gross_earnings}
                          onChange={(e) =>
                            handleChange(
                              "gross_earnings",
                              e.target.value,
                              "p_pay_rates",
                            )
                          }
                        />
                        <Form.Select
                          value={form.p_pay_rates.earnings_freq}
                          onChange={(e) =>
                            handleChange(
                              "earnings_freq",
                              e.target.value,
                              "p_pay_rates",
                            )
                          }
                        >
                          <option value="Weekly">Weekly</option>
                          <option value="Monthly">Monthly</option>
                          <option value="Yearly">Yearly</option>
                        </Form.Select>
                      </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label>Avg Hours Per Day</Form.Label>
                      <Form.Control
                        type="number"
                        value={form.p_pay_rates.hours_per_day}
                        onChange={(e) =>
                          handleChange(
                            "hours_per_day",
                            e.target.value,
                            "p_pay_rates",
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Driver Requirements</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Label>Demerit Points Allowed</Form.Label>
                    <Form.Control
                      value={form.p_driver_reqs.demerit}
                      onChange={(e) =>
                        handleChange("demerit", e.target.value, "p_driver_reqs")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Merit Points Required</Form.Label>
                    <Form.Control
                      value={form.p_driver_reqs.merit}
                      onChange={(e) =>
                        handleChange("merit", e.target.value, "p_driver_reqs")
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>CVOR Allowed</Form.Label>
                    <Form.Control
                      value={form.p_driver_reqs.cvor}
                      onChange={(e) =>
                        handleChange("cvor", e.target.value, "p_driver_reqs")
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Years Of Experience</Form.Label>
                    <Form.Control
                      value={form.p_driver_reqs.years_exp}
                      onChange={(e) =>
                        handleChange(
                          "years_exp",
                          e.target.value,
                          "p_driver_reqs",
                        )
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={4} className="mt-3">
                    <Form.Label>FAST Card Required?</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        checked={form.p_driver_reqs.fast_card === true}
                        onChange={() =>
                          handleChange("fast_card", true, "p_driver_reqs")
                        }
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        checked={form.p_driver_reqs.fast_card === false}
                        onChange={() =>
                          handleChange("fast_card", false, "p_driver_reqs")
                        }
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Orientation</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Label>Orientation</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        checked={form.p_orientation.orientation === true}
                        onChange={() =>
                          handleChange("orientation", true, "p_orientation")
                        }
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        checked={form.p_orientation.orientation === false}
                        onChange={() =>
                          handleChange("orientation", false, "p_orientation")
                        }
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Length</Form.Label>
                    <Form.Select
                      value={form.p_orientation.length}
                      onChange={(e) =>
                        handleChange("length", e.target.value, "p_orientation")
                      }
                    >
                      <option value="">Select</option>
                      <option value="1-2 days">1-2 days</option>
                      <option value="3-5 days">3-5 days</option>
                      <option value="1 week">1 week</option>
                    </Form.Select>
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col md={6}>
                    <Form.Label>Hotel Accommodation Provided?</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        checked={form.p_orientation.hotel === true}
                        onChange={() =>
                          handleChange("hotel", true, "p_orientation")
                        }
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        checked={form.p_orientation.hotel === false}
                        onChange={() =>
                          handleChange("hotel", false, "p_orientation")
                        }
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Province</Form.Label>
                    <Form.Control
                      value={form.p_orientation.province}
                      onChange={(e) =>
                        handleChange(
                          "province",
                          e.target.value,
                          "p_orientation",
                        )
                      }
                    />
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col md={6}>
                    <Form.Label>Paid</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        checked={form.p_orientation.paid === true}
                        onChange={() =>
                          handleChange("paid", true, "p_orientation")
                        }
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        checked={form.p_orientation.paid === false}
                        onChange={() =>
                          handleChange("paid", false, "p_orientation")
                        }
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            <Card className="mb-3">
              <Card.Header>Division Policies</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Label>Mentoring Provided</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        checked={form.p_division_policies.mentoring === true}
                        onChange={() =>
                          handleChange("mentoring", true, "p_division_policies")
                        }
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        checked={form.p_division_policies.mentoring === false}
                        onChange={() =>
                          handleChange(
                            "mentoring",
                            false,
                            "p_division_policies",
                          )
                        }
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Pets Policy</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        checked={form.p_division_policies.pets === true}
                        onChange={() =>
                          handleChange("pets", true, "p_division_policies")
                        }
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        checked={form.p_division_policies.pets === false}
                        onChange={() =>
                          handleChange("pets", false, "p_division_policies")
                        }
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Label>Riders Policy</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        checked={form.p_division_policies.riders === true}
                        onChange={() =>
                          handleChange("riders", true, "p_division_policies")
                        }
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        checked={form.p_division_policies.riders === false}
                        onChange={() =>
                          handleChange("riders", false, "p_division_policies")
                        }
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Contractors Policy</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        checked={form.p_division_policies.contractors === true}
                        onChange={() =>
                          handleChange(
                            "contractors",
                            true,
                            "p_division_policies",
                          )
                        }
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        checked={form.p_division_policies.contractors === false}
                        onChange={() =>
                          handleChange(
                            "contractors",
                            false,
                            "p_division_policies",
                          )
                        }
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Save / Cancel Buttons */}
            <div className="d-flex justify-content-between mb-4">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  const confirmed = window.confirm(
                    "Are you sure you want to cancel? Unsaved changes will be lost.",
                  );
                  if (confirmed) {
                    setShowDrawer(false);
                  }
                }}
              >
                Cancel
              </Button>
              <Button type="submit">Save Position</Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Status & Dates</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* ✅ POSITION INFO (READ-ONLY) */}
          {statusTarget && (
            <div className="mb-3 p-3 border rounded bg-light">
              <Row>
                <Col md={4}>
                  <strong>Position #:</strong>
                  <div>{statusTarget.p_position_number}</div>
                </Col>
                <Col md={8}>
                  <strong>Position Name:</strong>
                  <div>{statusTarget.p_position_title}</div>
                </Col>
              </Row>
            </div>
          )}

          <Form>
            {/* STATUS */}
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={statusForm.p_status}
                onChange={(e) =>
                  setStatusForm((prev) => ({
                    ...prev,
                    p_status: Number(e.target.value),
                  }))
                }
              >
                <option value={1}>Open</option>
                <option value={0}>Closed</option>
              </Form.Select>
            </Form.Group>

            {/* DATE POSTED */}
            <Form.Group className="mb-3">
              <Form.Label>Date Posted</Form.Label>
              <Form.Control
                type="date"
                value={statusForm.p_date_posted || ""}
                onChange={(e) =>
                  setStatusForm((prev) => ({
                    ...prev,
                    p_date_posted: e.target.value,
                  }))
                }
              />
            </Form.Group>

            {/* CLOSING DATE */}
            <Form.Group>
              <Form.Label>Closing Date</Form.Label>
              <Form.Control
                type="date"
                value={statusForm.p_closing_date || ""}
                onChange={(e) =>
                  setStatusForm((prev) => ({
                    ...prev,
                    p_closing_date: e.target.value,
                  }))
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={async () => {
              if (!statusTarget) return;

              const token = localStorage.getItem("token");
              const headers = { Authorization: `Bearer ${token}` };

              const payload = {
                p_status: statusForm.p_status,
                p_posting: {
                  ...statusTarget.p_posting,
                  p_date_posted: statusForm.p_date_posted || null,
                  p_closing_date: statusForm.p_closing_date || null,
                },
              };

              try {
                await axios.put(
                  `${BASE_URL}/api/positions/${statusTarget.p_id}`,
                  payload,
                  { headers },
                );

                setShowStatusModal(false);
                setStatusTarget(null);
                fetchData();
              } catch (err) {
                console.error("❌ Failed to update status", err);
                alert("Failed to update status");
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default PositionPage;
